/**
 * 共通タイポグラフィ定数
 * アプリケーション全体で使用するテキストサイズ・フォントウェイト等の定義
 */

/**
 * フォントサイズ（px）
 */
export const FONT_SIZES = {
  /** 極小サイズ（10px） - ラベルや注釈など */
  XS: 10,
  /** 小サイズ（12px） - サブテキストやメタ情報 */
  SM: 12,
  /** 基本サイズ（14px） - 通常のテキスト */
  BASE: 14,
  /** 中サイズ（16px） - 少し強調したいテキスト */
  MD: 16,
  /** 大サイズ（18px） - セクションタイトルなど */
  LG: 18,
  /** 特大サイズ（20px） - ヘッダータイトルなど */
  XL: 20,
  /** 2倍特大サイズ（24px） - 主要タイトル */
  XXL: 24,
  /** 3倍特大サイズ（32px） - ページタイトル */
  XXXL: 32,
} as const;

/**
 * アイコンサイズ（px）
 */
export const ICON_SIZES = {
  /** 小アイコン（16px） */
  SM: 16,
  /** 中アイコン（24px） */
  MD: 24,
  /** 大アイコン（32px） */
  LG: 32,
  /** 特大アイコン（48px） */
  XL: 48,
  /** 2倍特大アイコン（64px） */
  XXL: 64,
} as const;

/**
 * フォントウェイト
 */
export const FONT_WEIGHTS = {
  /** 細字 */
  LIGHT: 300,
  /** 通常 */
  NORMAL: 400,
  /** 中太字 */
  MEDIUM: 500,
  /** 太字 */
  SEMIBOLD: 600,
  /** 極太字 */
  BOLD: 700,
} as const;

/**
 * 行の高さ
 */
export const LINE_HEIGHTS = {
  /** コンパクト（1.2） */
  COMPACT: 1.2,
  /** タイト（1.4） */
  TIGHT: 1.4,
  /** 通常（1.5） */
  NORMAL: 1.5,
  /** リラックス（1.7） */
  RELAXED: 1.7,
  /** ルーズ（2.0） */
  LOOSE: 2.0,
} as const;

/**
 * Tailwindクラス名マッピング
 */
export const TEXT_CLASSES = {
  /** テキストサイズクラス */
  SIZE: {
    XS: 'text-xs',
    SM: 'text-sm',
    BASE: 'text-base',
    LG: 'text-lg',
    XL: 'text-xl',
    XXL: 'text-2xl',
    XXXL: 'text-3xl',
  },
  /** フォントウェイトクラス */
  WEIGHT: {
    LIGHT: 'font-light',
    NORMAL: 'font-normal',
    MEDIUM: 'font-medium',
    SEMIBOLD: 'font-semibold',
    BOLD: 'font-bold',
  },
} as const;

/**
 * タイポグラフィプリセット
 */
export const TYPOGRAPHY_PRESETS = {
  /** ページタイトル */
  PAGE_TITLE: {
    fontSize: FONT_SIZES.XXL,
    fontWeight: FONT_WEIGHTS.BOLD,
    lineHeight: LINE_HEIGHTS.TIGHT,
  },
  /** セクションタイトル */
  SECTION_TITLE: {
    fontSize: FONT_SIZES.LG,
    fontWeight: FONT_WEIGHTS.SEMIBOLD,
    lineHeight: LINE_HEIGHTS.TIGHT,
  },
  /** カードタイトル */
  CARD_TITLE: {
    fontSize: FONT_SIZES.MD,
    fontWeight: FONT_WEIGHTS.SEMIBOLD,
    lineHeight: LINE_HEIGHTS.NORMAL,
  },
  /** 本文 */
  BODY: {
    fontSize: FONT_SIZES.BASE,
    fontWeight: FONT_WEIGHTS.NORMAL,
    lineHeight: LINE_HEIGHTS.NORMAL,
  },
  /** サブテキスト */
  SUB_TEXT: {
    fontSize: FONT_SIZES.SM,
    fontWeight: FONT_WEIGHTS.NORMAL,
    lineHeight: LINE_HEIGHTS.NORMAL,
  },
  /** キャプション */
  CAPTION: {
    fontSize: FONT_SIZES.XS,
    fontWeight: FONT_WEIGHTS.NORMAL,
    lineHeight: LINE_HEIGHTS.TIGHT,
  },
  /** ラベル */
  LABEL: {
    fontSize: FONT_SIZES.SM,
    fontWeight: FONT_WEIGHTS.MEDIUM,
    lineHeight: LINE_HEIGHTS.TIGHT,
  },
} as const;

/**
 * タイポグラフィユーティリティ
 */
export const TypographyUtils = {
  /**
   * px値をrem値に変換
   */
  pxToRem: (px: number, baseFontSize: number = 16): string => {
    return `${px / baseFontSize}rem`;
  },

  /**
   * フォントサイズスタイルオブジェクトを生成
   */
  getFontSizeStyle: (size: keyof typeof FONT_SIZES): { fontSize: string } => {
    return { fontSize: `${FONT_SIZES[size]}px` };
  },

  /**
   * アイコンサイズスタイルオブジェクトを生成
   */
  getIconSizeStyle: (size: keyof typeof ICON_SIZES): { fontSize: number } => {
    return { fontSize: ICON_SIZES[size] };
  },

  /**
   * プリセットスタイルオブジェクトを生成
   */
  getPresetStyle: (preset: keyof typeof TYPOGRAPHY_PRESETS) => {
    const presetValues = TYPOGRAPHY_PRESETS[preset];
    return {
      fontSize: `${presetValues.fontSize}px`,
      fontWeight: presetValues.fontWeight,
      lineHeight: presetValues.lineHeight,
    };
  },
} as const;

/**
 * 型定義
 */
export type FontSize = typeof FONT_SIZES[keyof typeof FONT_SIZES];
export type IconSize = typeof ICON_SIZES[keyof typeof ICON_SIZES];
export type FontWeight = typeof FONT_WEIGHTS[keyof typeof FONT_WEIGHTS];
export type LineHeight = typeof LINE_HEIGHTS[keyof typeof LINE_HEIGHTS];
export type TypographyPreset = keyof typeof TYPOGRAPHY_PRESETS;